import { useEffect, useState, useRef } from 'react';
import { PageHeader, SectionLabel, Card, SampleBanner, StatusPill } from '../components/ui.js';

interface FeedEvent {
  id: string;
  ts: string;
  account: string;
  type: 'content_published' | 'gbp_post' | 'review_replied' | 'audit_finding' | 'auto_fix' | 'backup' | 'qa_pass' | 'qa_warn' | 'rank_movement' | 'aieo_citation';
  title: string;
  detail?: string;
}

const TYPE_TONE: Record<FeedEvent['type'], 'ok' | 'warn' | 'bad' | 'neutral' | 'info'> = {
  content_published: 'ok',
  gbp_post: 'info',
  review_replied: 'ok',
  audit_finding: 'warn',
  auto_fix: 'info',
  backup: 'ok',
  qa_pass: 'ok',
  qa_warn: 'warn',
  rank_movement: 'info',
  aieo_citation: 'ok',
};

const TYPE_LABEL: Record<FeedEvent['type'], string> = {
  content_published: 'content',
  gbp_post: 'gbp',
  review_replied: 'reply',
  audit_finding: 'audit',
  auto_fix: 'auto-fix',
  backup: 'backup',
  qa_pass: 'qa',
  qa_warn: 'qa',
  rank_movement: 'rank',
  aieo_citation: 'aieo',
};

const SEED: FeedEvent[] = [
  { id: 'e-100', ts: new Date(Date.now() - 90_000).toISOString(), account: 'Glisten Mesa', type: 'content_published', title: 'Published "Why Mesa Families Choose Composite Fillings for Kids"', detail: '1,320 words · AI-tell 11 · Bloom Score 82' },
  { id: 'e-099', ts: new Date(Date.now() - 4 * 60_000).toISOString(), account: 'Glisten Gilbert', type: 'review_replied', title: 'Bloom drafted reply to 5★ review from Maria S.', detail: 'awaiting your approval' },
  { id: 'e-098', ts: new Date(Date.now() - 12 * 60_000).toISOString(), account: 'Glisten Glendale', type: 'rank_movement', title: '"sedation dentistry glendale" up to #8 (+1)' },
  { id: 'e-097', ts: new Date(Date.now() - 28 * 60_000).toISOString(), account: 'Glisten Gilbert', type: 'aieo_citation', title: 'Cited by Perplexity for "best dentist in gilbert az"', detail: '6th citation this month' },
  { id: 'e-096', ts: new Date(Date.now() - 47 * 60_000).toISOString(), account: 'Glisten Mesa', type: 'auto_fix', title: 'Auto-fixed: 4 title tags trimmed under 60 chars', detail: 'audit check #14' },
  { id: 'e-095', ts: new Date(Date.now() - 73 * 60_000).toISOString(), account: 'Glisten Glendale', type: 'gbp_post', title: 'Posted: "Now offering Saturday appointments by request"' },
  { id: 'e-094', ts: new Date(Date.now() - 95 * 60_000).toISOString(), account: 'Glisten Gilbert', type: 'audit_finding', title: 'Audit found: 4 missing cluster links from Implants pillar', detail: 'queued for auto-fix' },
  { id: 'e-093', ts: new Date(Date.now() - 132 * 60_000).toISOString(), account: 'Glisten Mesa', type: 'qa_pass', title: 'Daily QA passed (24/24 checks)' },
  { id: 'e-092', ts: new Date(Date.now() - 158 * 60_000).toISOString(), account: 'Glisten Glendale', type: 'qa_pass', title: 'Daily QA passed (24/24 checks)' },
  { id: 'e-091', ts: new Date(Date.now() - 175 * 60_000).toISOString(), account: 'Glisten Gilbert', type: 'qa_pass', title: 'Daily QA passed (24/24 checks)' },
  { id: 'e-090', ts: new Date(Date.now() - 6 * 60 * 60_000).toISOString(), account: 'Glisten Glendale', type: 'backup', title: 'Nightly backup verified', detail: 'DB 184 MB · files 2.84 GB' },
  { id: 'e-089', ts: new Date(Date.now() - 7 * 60 * 60_000).toISOString(), account: 'Glisten Mesa', type: 'backup', title: 'Nightly backup verified', detail: 'DB 192 MB · files 2.91 GB' },
  { id: 'e-088', ts: new Date(Date.now() - 7.5 * 60 * 60_000).toISOString(), account: 'Glisten Gilbert', type: 'backup', title: 'Nightly backup verified', detail: 'DB 188 MB · files 2.78 GB' },
];

const TICK_POOL: Omit<FeedEvent, 'id' | 'ts'>[] = [
  { account: 'Glisten Gilbert', type: 'aieo_citation', title: 'Cited by ChatGPT for "emergency dentist gilbert open saturday"' },
  { account: 'Glisten Mesa', type: 'rank_movement', title: '"ahcccs dentist mesa" up to #2 (+1)' },
  { account: 'Glisten Glendale', type: 'review_replied', title: 'Bloom drafted reply to 4★ review from Patricia K.', detail: 'awaiting your approval' },
  { account: 'Glisten Mesa', type: 'gbp_post', title: 'Posted: "Whitening special — in-office Zoom for $399"' },
  { account: 'Glisten Gilbert', type: 'auto_fix', title: 'Auto-fixed: FAQPage schema added to /implants', detail: 'audit check #6' },
  { account: 'Glisten Glendale', type: 'rank_movement', title: '"same day crowns glendale" up to #6 (+5)' },
  { account: 'Glisten Mesa', type: 'aieo_citation', title: 'Cited by Claude for "dentist that takes ahcccs mesa"' },
];

export function OperatorFeedPage() {
  const [events, setEvents] = useState<FeedEvent[]>(SEED);
  const [paused, setPaused] = useState(false);
  const seqRef = useRef(101);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      const pick = TICK_POOL[Math.floor(Math.random() * TICK_POOL.length)]!;
      const ev: FeedEvent = {
        ...pick,
        id: `e-${seqRef.current++}`,
        ts: new Date().toISOString(),
      };
      setEvents((prev) => [ev, ...prev].slice(0, 60));
    }, 8000);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div className="space-y-12">
      <PageHeader
        eyebrow="Operator · Real-time Feed"
        title={<>Everything Bloom is doing, <em className="text-cream/80">live.</em></>}
        subtitle="A continuous stream of every action the engine takes across every customer. Filter by type or pause to read."
        action={
          <div className="flex items-center gap-3">
            {!paused ? (
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-300/85">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/80 animate-pulse" />
                Live
              </span>
            ) : (
              <span className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-cream/55">
                <span className="w-1.5 h-1.5 rounded-full bg-cream/40" />
                Paused
              </span>
            )}
            <button type="button" onClick={() => setPaused((p) => !p)} className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-cream/70 hover:text-cream border border-cream/[0.12] hover:border-cream/[0.3] transition-colors">
              {paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        }
      />
      <SampleBanner note="Sample feed with synthetic events ticking every 8s. Real implementation = SSE stream from bloom-api." />

      <section>
        <SectionLabel>Recent events ({events.length})</SectionLabel>
        <div className="space-y-2">
          {events.map((ev) => (
            <Card key={ev.id}>
              <div className="flex items-start gap-4">
                <div className="shrink-0 mt-0.5">
                  <StatusPill tone={TYPE_TONE[ev.type]}>{TYPE_LABEL[ev.type]}</StatusPill>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-cream/90 text-sm">{ev.title}</span>
                    <span className="text-[11px] text-cream/40">· {ev.account}</span>
                  </div>
                  {ev.detail && <div className="text-[11px] text-cream/55 mt-1">{ev.detail}</div>}
                </div>
                <div className="shrink-0 text-[11px] text-cream/40 tabular-nums">{relativeTime(ev.ts)}</div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 30_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86400_000) return `${Math.floor(ms / 3600_000)}h ago`;
  return `${Math.floor(ms / 86400_000)}d ago`;
}
